import React, { useEffect, useMemo } from 'react';
import {
  Box, Container, styled, ThemeProvider,
} from '@material-ui/core';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';

import { SleepioHeader } from '../Onboarding/components/Header/SleepioHeader';
import { getPlatform } from '../Onboarding/utils/platformHelpers';
import SleepioWellnessOnboarding from './SleepioWellnessOnboarding';
import NotFoundError from './steps/ErrorState/NotFoundError';
import { Loader } from './components';

import { Footer } from '../Onboarding/components/Footer/Footer';

import theme from '@/themes/sleepioTheme';
import { getPdtUid, Platform, Product } from '@/utils/productHelpers';
import { useConsent } from '@/hooks/useConsent';
import { ELIGIBILITY_CONFIGURATION_QUERY } from '@/apollo/queries';
import { buildAppDeepLink } from '@/utils/urlHelpers';

export type SleepioWellnessOnboardingParams = {
  slug: string;
  careTeamUid: string;
  token: string;
  product: Product;
}

const ContentContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: '100vh',
});

const SleepioWellnessOnboardingPage = () => {
  useConsent();
  const { slug, product } = useParams<SleepioWellnessOnboardingParams>();

  // Chrome keeps in-page taps to itself; redirect_uri means the app opened this page, so skip it then.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('redirect_uri') || getPlatform() === Platform.Web) {
      return;
    }
    // Runs before the token is validated, so an expired link surfaces ExpiredLinkError inside the app.
    const deepLink = buildAppDeepLink(product, window.location.href);
    if (deepLink) {
      window.location.href = deepLink;
    }
  }, [product]);

  const {
    loading,
    data,
  } = useQuery(ELIGIBILITY_CONFIGURATION_QUERY, {
    variables: {
      organizationSlug: slug,
      pdtUid: getPdtUid(product),
    },
  });

  const config = useMemo(() => data?.eligibilityConfiguration, [data]);

  const eligibilityConfiguration = useMemo(() => ({
    showZipCode: config?.collectZipcodeForEligibility,
    showEmployeeId: config?.hasEmployeeId,
    showAddressField: config?.hasAddressField,
    employeeIdFieldRename: config?.employeeIdFieldRename,
    isGeneric: config?.isGeneric,
    employeeIdValidationEnabled: config?.employeeIdValidationEnabled,
    employeeIdAllowedChars: config?.employeeIdAllowedChars,
    employeeIdMinLength: config?.employeeIdMinLength,
    employeeIdMaxLength: config?.employeeIdMaxLength,
    employeeIdValidationErrorMessage: config?.employeeIdValidationErrorMessage,
    employeeIdExample: config?.employeeIdExample,
  }), [config]);

  if (loading) {
    return <Loader />;
  }

  if (!config.isValid) {
    return <NotFoundError />;
  }

  return (
    <ContentContainer>
      <Container maxWidth="sm">
        <SleepioWellnessOnboarding
          formCustomization={eligibilityConfiguration}
        />
      </Container>
      <Footer />
    </ContentContainer>
  );
};

export default () => (
  <ThemeProvider theme={theme}>
    <SleepioHeader />
    <SleepioWellnessOnboardingPage />
  </ThemeProvider>
);
